import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  UsersIcon,
  BanknotesIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowRightIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

const Dashboard = () => {
  const { tenantName, isTenantAdmin } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/users/dashboard');
      setDashboardData(response.data.dashboard);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  const formatCurrency = (amount, currency = 'ZAR') => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const StatCard = ({ icon: Icon, label, value, color = 'primary', trend }) => (
    <div className="stat-card group">
      <div className="flex items-start justify-between">
        <div className={`stat-icon bg-${color}-50 text-${color}-500`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <div className="flex items-center gap-1 text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
            <ArrowTrendingUpIcon className="w-3 h-3" />
            {trend}
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="stat-value">{value}</p>
        <p className="stat-label">{label}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          Welcome back, <span className="text-primary-500">{dashboardData?.user?.firstName || 'User'}</span>
        </h1>
        <p className="page-subtitle">
          {tenantName ? `${tenantName} — ` : ''}Here's an overview of your dashboard
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
        <StatCard
          icon={CreditCardIcon}
          label="Active Plan"
          value={dashboardData?.subscription?.planName || 'No Plan'}
          color="primary"
        />
        <StatCard
          icon={ChatBubbleLeftRightIcon}
          label="Workspaces"
          value={dashboardData?.stats?.totalWorkspaces || 0}
          color="green"
        />
        <StatCard
          icon={UsersIcon}
          label="Team Members"
          value={dashboardData?.stats?.teamMembers || 0}
          color="purple"
        />
        <StatCard
          icon={BanknotesIcon}
          label="Messages Used"
          value={dashboardData?.stats?.messagesUsed || 0}
          color="yellow"
        />
      </div>

      {/* Current Subscription */}
      {!dashboardData?.subscription && (
        <div className="card text-center py-8">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-50 flex items-center justify-center mb-4">
            <CreditCardIcon className="w-7 h-7 text-primary-500" />
          </div>
          <h3 className="text-lg font-semibold text-neutral-800">No Active Subscription</h3>
          <p className="mt-2 text-sm text-neutral-500 max-w-sm mx-auto">
            Subscribe to a plan to unlock chatbot workspaces, team management, and more.
          </p>
          <div className="mt-5">
            <Link to="/subscriptions" className="btn-primary inline-flex items-center gap-2">
              View Plans
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
      {dashboardData?.subscription && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-neutral-800">Current Subscription</h3>
              <p className="text-sm text-neutral-500 mt-1">Your active subscription details</p>
            </div>
            <span className={`badge ${
              dashboardData.subscription.status === 'active' ? 'badge-green' : 'badge-yellow'
            }`}>
              {dashboardData.subscription.status}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <dt className="text-sm font-medium text-neutral-500">Plan</dt>
              <dd className="text-base font-semibold text-neutral-800">
                {dashboardData.subscription.planName}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-sm font-medium text-neutral-500">Price</dt>
              <dd className="text-base font-semibold text-neutral-800">
                {formatCurrency(dashboardData.subscription.price, dashboardData.subscription.currency || 'ZAR')}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-sm font-medium text-neutral-500">End Date</dt>
              <dd className="text-base font-semibold text-neutral-800">
                {formatDate(dashboardData.subscription.endDate)}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-sm font-medium text-neutral-500">Start Date</dt>
              <dd className="text-sm text-neutral-600">
                {formatDate(dashboardData.subscription.startDate)}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-sm font-medium text-neutral-500">Auto Renew</dt>
              <dd className="flex items-center gap-2">
                {dashboardData.subscription.autoRenew ? (
                  <>
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                    <span className="text-sm text-green-600 font-medium">Enabled</span>
                  </>
                ) : (
                  <>
                    <XCircleIcon className="h-5 w-5 text-neutral-400" />
                    <span className="text-sm text-neutral-500">Disabled</span>
                  </>
                )}
              </dd>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-neutral-200">
            <Link
              to="/subscriptions"
              className="btn-outline btn-sm inline-flex items-center gap-2"
            >
              Manage Subscription
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Chatbot Workspaces */}
      {dashboardData?.workspaces && dashboardData.workspaces.length > 0 && (
        <div className="card bg-gradient-to-br from-white to-primary-50/30">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-neutral-800">Chatbot Workspaces</h3>
              <p className="text-sm text-neutral-500 mt-1">Your automation hubs</p>
            </div>
          </div>

          <div className="space-y-3">
            {dashboardData.workspaces.map((workspace) => (
              <div key={workspace.id} className="flex items-center justify-between p-4 bg-white rounded-lg border border-neutral-100">
                <div>
                  <p className="text-sm font-semibold text-neutral-800">{workspace.name}</p>
                  <span className="badge badge-green text-xs">{workspace.status}</span>
                </div>
                <Link
                  to="/typebot"
                  className="btn-primary btn-sm inline-flex items-center gap-1"
                >
                  Manage <ArrowRightIcon className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-neutral-200">
            <Link to="/typebot" className="btn-secondary btn-sm">
              Manage Chatbots
            </Link>
          </div>
        </div>
      )}

      {/* Recent Payments */}
      {dashboardData?.recentPayments && dashboardData.recentPayments.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-neutral-800">Recent Payments</h3>
              <p className="text-sm text-neutral-500 mt-1">Your latest transactions</p>
            </div>
            <Link
              to="/payments"
              className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1"
            >
              View all
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>

          <div className="flow-root">
            <ul className="-mb-4">
              {dashboardData.recentPayments.map((payment, index) => (
                <li key={payment.id} className="pb-4">
                  <div className="relative flex items-start gap-4">
                    {index !== dashboardData.recentPayments.length - 1 && (
                      <span className="absolute top-10 left-4 -ml-px h-full w-0.5 bg-neutral-200" aria-hidden="true" />
                    )}
                    <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-white ${
                      payment.status === 'success' ? 'bg-green-500' :
                      payment.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                    }`}>
                      {payment.status === 'success' ? (
                        <CheckCircleIcon className="h-4 w-4 text-white" />
                      ) : payment.status === 'pending' ? (
                        <ClockIcon className="h-4 w-4 text-white" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 text-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-800">
                        Payment for <span className="text-primary-600">{payment.planName || 'Subscription'}</span>
                      </p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        Ref: {payment.reference}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-neutral-800">
                        {formatCurrency(payment.amount, payment.currency)}
                      </p>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {formatDate(payment.createdAt)}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-lg font-semibold text-neutral-800 mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              to: '/subscriptions',
              icon: CreditCardIcon,
              title: 'Manage Subscriptions',
              description: 'View and manage your plans',
              color: 'primary',
            },
            {
              to: '/typebot',
              icon: ChatBubbleLeftRightIcon,
              title: 'Chatbot Workspace',
              description: 'Build and manage chatbots',
              color: 'green',
            },
            ...(isTenantAdmin ? [{
              to: '/team',
              icon: UsersIcon,
              title: 'Team Management',
              description: 'Invite and manage members',
              color: 'purple',
            }] : []),
            {
              to: '/payments',
              icon: BanknotesIcon,
              title: 'Payment History',
              description: 'View all transactions',
              color: 'yellow',
            },
          ].map((action, index) => (
            <Link
              key={action.to}
              to={action.to}
              className="group relative p-5 rounded-xl border border-neutral-200 bg-white hover:border-primary-300 hover:shadow-md transition-all duration-300"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className={`w-12 h-12 rounded-xl bg-${action.color}-50 text-${action.color}-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <action.icon className="w-6 h-6" />
              </div>
              <h4 className="text-base font-semibold text-neutral-800 group-hover:text-primary-600 transition-colors">
                {action.title}
              </h4>
              <p className="mt-1 text-sm text-neutral-500">
                {action.description}
              </p>
              <ArrowRightIcon className="absolute top-5 right-5 w-4 h-4 text-neutral-300 group-hover:text-primary-500 group-hover:translate-x-1 transition-all" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
